import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { stripeAPI } from '@/services/stripeAPI';
import { accountServiceAPI } from '@/services/accountServiceAPI';
import { singleSearchAPI } from '@/services/singleSearchAPI';
import { customerAPI } from '@/services/customerAPI';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { SavedPaymentMethodConfirmation } from '@/components/payment/SavedPaymentMethodConfirmation';
import { SearchPaymentCheckoutForm } from '@/components/search/payment/SearchPaymentCheckoutForm';
import { SearchPricingSummary } from '@/components/search/payment/SearchPricingSummary';
import { SearchNoHitResults } from '@/components/search/payment/SearchNoHitResults';
import { SearchWalletPayment } from '@/components/search/payment/SearchWalletPayment';
import { SearchCustomerInfoForm } from '@/components/search/payment/SearchCustomerInfoForm';
import type { SingleSearchRequest } from '@/types';
import { getSearchTypeDisplayName, formatCurrency, getSearchTypePrice } from '@/utils/searchValidation';
import { getWalletSearchCost, getOneTimeSearchCost } from '@/config/pricing';
import { logger, NoHitError } from '@/utils/logger';
import { ArrowLeft, CheckCircle, XCircle, User, Mail, CreditCard, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  throw new Error('VITE_STRIPE_PUBLISHABLE_KEY environment variable is required');
}

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface CustomerInfo {
  name: string;
  email: string;
  companyName: string;
}

interface AIOverview {
  overview: string;
  riskAssessment: string;
  recommendedStrategy: string;
}

export const SearchPaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchId = searchParams.get('searchId');
  const { isAuthenticated, user, signIn, refreshUser } = useCustomerAuth();

  // Fetch saved payment methods for authenticated users
  const { data: savedPaymentMethods } = useQuery({
    queryKey: ['payment-methods', user?.userId],
    queryFn: () => customerAPI.getPaymentMethods(user!.userId),
    enabled: isAuthenticated && !!user?.userId,
  });

  // Fetch wallet balance for authenticated users
  const { data: walletBalance = 0 } = useQuery({
    queryKey: ['wallet-balance', user?.email],
    queryFn: async () => {
      if (!user?.email) {
        throw new Error('User email is required to fetch wallet balance');
      }
      return await stripeAPI.getWalletBalance(user.email);
    },
    enabled: isAuthenticated && !!user?.email,
  });

  // Get the default payment method
  const defaultPaymentMethod = savedPaymentMethods?.find(pm => pm.isDefault) || savedPaymentMethods?.[0];

  const [searchRequest, setSearchRequest] = useState<SingleSearchRequest | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: user?.name || '',
    email: user?.email || '',
    companyName: user?.companyName || ''
  });
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoadingSearch, setIsLoadingSearch] = useState(true);
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noHitMessage, setNoHitMessage] = useState<string | null>(null);
  const [noHitAiOverview, setNoHitAiOverview] = useState<AIOverview | null>(null);
  const [customerInfoErrors, setCustomerInfoErrors] = useState<{ name?: string; email?: string; companyName?: string }>({});
  const [useSavedPaymentMethod, setUseSavedPaymentMethod] = useState(true); // Start with saved payment method if available
  const [accountMode, setAccountMode] = useState<'create' | 'login'>('create');
  const [accountSetupComplete, setAccountSetupComplete] = useState(false);
  const [accountPassword, setAccountPassword] = useState('');
  const [accountPasswordConfirm, setAccountPasswordConfirm] = useState('');
  const [accountPasswordError, setAccountPasswordError] = useState<string | null>(null);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const [accountFeedback, setAccountFeedback] = useState<string | null>(null);
  const [walletFcraAcknowledgment, setWalletFcraAcknowledgment] = useState(false);

  // Calculate dynamic pricing based on wallet balance
  const calculateSearchCost = (searchType: string) => {
    if (!searchRequest) return { total: 0, walletDeduction: 0, stripeCharge: 0, useWallet: false };

    const walletPrice = getWalletSearchCost(searchType as any);
    const oneTimePrice = getOneTimeSearchCost(searchType as any);

    // If authenticated and has wallet balance
    if (isAuthenticated && walletBalance > 0) {
      if (walletBalance >= walletPrice) {
        // Full wallet payment
        return {
          total: walletPrice,
          walletDeduction: walletPrice,
          stripeCharge: 0,
          useWallet: true,
          savingsVsOneTime: oneTimePrice - walletPrice,
        };
      } else {
        // Partial wallet + Stripe for remainder (charge one-time price, apply wallet as discount)
        const stripeAmount = oneTimePrice - walletBalance;
        return {
          total: oneTimePrice,
          walletDeduction: walletBalance,
          stripeCharge: stripeAmount,
          useWallet: true,
          savingsVsOneTime: walletBalance,
        };
      }
    }

    // No wallet or not authenticated - charge one-time price
    return {
      total: oneTimePrice,
      walletDeduction: 0,
      stripeCharge: oneTimePrice,
      useWallet: false,
      savingsVsOneTime: 0,
    };
  };

  useEffect(() => {
    if (!searchId) {
      logger.error('No search ID provided in payment page');
      navigate('/search');
      return;
    }

    const loadSearchRequest = async () => {
      try {
        logger.info('Loading search request from sessionStorage', { searchId });

        // Load search request from sessionStorage
        const storedRequest = sessionStorage.getItem(`search_request_${searchId}`);
        if (!storedRequest) {
          logger.error('Search request not found in sessionStorage', { searchId });
          setError('Search request expired. Please start a new search.');
          setIsLoadingSearch(false);
          return;
        }

        const request: SingleSearchRequest = JSON.parse(storedRequest);
        setSearchRequest(request);
        setIsLoadingSearch(false);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load search request';
        logger.error('Failed to load search request:', err);
        setError(errorMessage);
        setIsLoadingSearch(false);
      }
    };

    loadSearchRequest();
  }, [searchId, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      setAccountSetupComplete(true);
      setAccountFeedback(null);
      setAccountPasswordError(null);
      setLoginError(null);
      return;
    }

    setAccountSetupComplete(false);
    setAccountMode('create');
    setAccountPassword('');
    setAccountPasswordConfirm('');
    setLoginPassword('');
    setAccountPasswordError(null);
    setLoginError(null);
    setAccountFeedback(null);
  }, [customerInfo.email, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    if (accountMode === 'login') {
      setSavePaymentMethod(false);
    }
  }, [accountMode, isAuthenticated]);

  const handleCustomerInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate customer info
    const errors: { name?: string; email?: string; companyName?: string } = {};
    if (!customerInfo.name || customerInfo.name.trim().length < 2) {
      errors.name = 'Please enter your full name';
    }
    if (!customerInfo.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!customerInfo.companyName || customerInfo.companyName.trim().length < 2) {
      errors.companyName = 'Please enter your company name';
    }

    if (Object.keys(errors).length > 0) {
      setCustomerInfoErrors(errors);
      return;
    }

    setCustomerInfoErrors({});
    setIsCreatingPayment(true);
    setError(null);

    try {
      if (!searchRequest) {
        throw new Error('Search request not loaded');
      }

      if (!isAuthenticated && !accountSetupComplete) {
        if (accountMode === 'create') {
          const password = accountPassword.trim();
          const confirm = accountPasswordConfirm.trim();

          if (password.length < 8) {
            setAccountPasswordError('Password must be at least 8 characters long.');
            setIsCreatingPayment(false);
            return;
          }

          if (password !== confirm) {
            setAccountPasswordError('Passwords do not match.');
            setIsCreatingPayment(false);
            return;
          }

          setAccountPasswordError(null);

          try {
            const nameTokens = customerInfo.name.trim().split(/\s+/);
            const firstName = nameTokens[0] || customerInfo.name.trim();
            const lastName = nameTokens.slice(1).join(' ');

            const inviteResponse = await accountServiceAPI.createAccountInvite({
              email: customerInfo.email.trim(),
              firstName,
              lastName,
              companyName: customerInfo.companyName.trim(),
              password,
            });

            if (inviteResponse.userExists) {
              setAccountMode('login');
              setAccountFeedback('An account with this email already exists. Please sign in to continue.');
              setIsCreatingPayment(false);
              return;
            }

            if (inviteResponse.success && inviteResponse.passwordSet) {
              try {
                // Small delay to allow Cognito to propagate user state
                await new Promise(resolve => setTimeout(resolve, 1000));

                logger.info('Attempting automatic sign-in after account creation', { email: customerInfo.email });
                await signIn(customerInfo.email.trim(), password);

                // Refresh user state in context
                refreshUser();

                logger.info('Automatic sign-in successful', { email: customerInfo.email });
                setAccountFeedback('Account created and you are now signed in. Continue to payment to finish your search.');
              } catch (autoErr) {
                logger.error('Automatic sign-in failed after account creation:', autoErr);
                const autoMessage =
                  autoErr instanceof Error
                    ? autoErr.message
                    : 'Automatic sign-in failed. Please sign in manually after finishing checkout.';
                setAccountFeedback(
                  `Account created successfully, but automatic sign-in failed: ${autoMessage}. You can sign in manually after checkout.`
                );
              }
            } else {
              setAccountFeedback(
                inviteResponse.message ||
                  'Account created. Complete checkout to access your dashboard and saved searches.'
              );
            }

            setAccountSetupComplete(true);
            setSavePaymentMethod(true);
            setAccountPassword('');
            setAccountPasswordConfirm('');
          } catch (inviteError) {
            const errorCode =
              typeof inviteError === 'object' && inviteError !== null && 'errorCode' in inviteError
                ? (inviteError as { errorCode?: string }).errorCode
                : undefined;
            const fallbackMessage =
              inviteError instanceof Error
                ? inviteError.message
                : 'We were unable to create your account. Please try again.';

            if (errorCode === 'PASSWORD_NOT_SET' || errorCode === 'PASSWORD_NOT_CONFIRMED') {
              setAccountFeedback(
                'We created your account but need you to finish the password setup from the invite email. Once done, sign in to continue.'
              );
              setAccountSetupComplete(true);
              setSavePaymentMethod(true);
            } else {
              setAccountPasswordError(fallbackMessage);
              setIsCreatingPayment(false);
              return;
            }
          }
        } else {
          if (!loginPassword) {
            setLoginError('Enter your password to sign in before checkout.');
            setIsCreatingPayment(false);
            return;
          }

          try {
            await signIn(customerInfo.email.trim(), loginPassword);
            refreshUser();
            setAccountSetupComplete(true);
            setAccountFeedback('Signed in successfully. Continue with payment to finish your search.');
            setLoginPassword('');
            setLoginError(null);
          } catch (loginErr) {
            const loginMessage =
              loginErr instanceof Error ? loginErr.message : 'Sign in failed. Please try again.';
            const normalizedMessage = loginMessage.includes('Password change required')
              ? 'Your account needs a new password before signing in. Complete the reset from the login page, then return here to finish checkout.'
              : loginMessage;
            setLoginError(normalizedMessage);
            setIsCreatingPayment(false);
            return;
          }
        }
      }

      logger.info('Creating payment intent with customer info', {
        searchId,
        customerName: customerInfo.name,
        customerEmail: customerInfo.email,
      });

      // Create payment intent via Stripe with customer billing info
      const price = getSearchTypePrice(searchRequest.searchType);
      const searchTypeName = getSearchTypeDisplayName(searchRequest.searchType);

      const paymentResponse = await stripeAPI.createSingleSearchPayment({
        amount: price,
        email: customerInfo.email,
        firstName: customerInfo.name.split(' ')[0] || customerInfo.name,
        lastName: customerInfo.name.split(' ').slice(1).join(' ') || customerInfo.name,
        searchType: searchRequest.searchType,
        amountToCollect: searchRequest.amountToCollect,
        description: `${searchTypeName} - ${searchRequest.firstName} ${searchRequest.lastName}`,
        companyName: customerInfo.companyName,
        savePaymentMethod: true,
      });

      setClientSecret(paymentResponse.client_secret);
      logger.info('Payment intent created successfully', {
        paymentIntentId: paymentResponse.payment_intent_id,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize payment';
      logger.error('Failed to initialize payment:', err);
      setError(errorMessage);
    } finally {
      setIsCreatingPayment(false);
    }
  };

  const handlePaymentSuccess = (resultSearchId: string) => {
    navigate(`/search/results/${resultSearchId}`);
  };

  const handleNoHit = (message: string, aiOverview: AIOverview) => {
    setNoHitMessage(message);
    setNoHitAiOverview(aiOverview);
  };

  const handleWalletPayment = async () => {
    if (!searchRequest || !user) {
      return;
    }

    setIsCreatingPayment(true);
    setError(null);

    try {
      const walletPrice = getWalletSearchCost(searchRequest.searchType);
      const searchTypeName = getSearchTypeDisplayName(searchRequest.searchType);

      logger.info('Processing wallet-funded search', {
        searchId,
        customerEmail: user.email,
        amount: walletPrice,
      });

      // Execute the search first
      const searchResult = await singleSearchAPI.createSearch(searchRequest);

      logger.info('Search completed successfully - determining charge amount', { searchId: searchResult.requestId });

      // Determine actual charge based on what succeeded
      // If scoring_enrich was requested but enrichment failed (no personalInfo), charge for scoring only
      let actualCharge = walletPrice;
      let actualSearchType = searchRequest.searchType;

      if (searchRequest.searchType === 'scoring_enrich' && !searchResult.personalInfo) {
        actualCharge = getWalletSearchCost('scoring');
        actualSearchType = 'scoring';
        logger.info('Scoring_enrich partial success - charging for scoring only', {
          originalPrice: walletPrice,
          actualPrice: actualCharge,
        });
      }

      const actualSearchTypeName = getSearchTypeDisplayName(actualSearchType);

      // Deduct from wallet balance
      await stripeAPI.walletDeduct(
        user.email,
        actualCharge,
        `Single Search - ${actualSearchTypeName} - ${searchRequest.firstName} ${searchRequest.lastName}`,
        {
          searchType: actualSearchType,
          searchId: searchResult.requestId,
        }
      );

      logger.info('Wallet deduction successful', { amount: actualCharge, chargedFor: actualSearchType });

      // Save search history to DynamoDB (non-blocking)
      try {
        if (user.userId && user.email) {
          logger.info('Saving search history to DynamoDB', {
            searchId: searchResult.requestId,
            cognitoUserId: user.userId,
            email: user.email
          });

          // Don't await - save in background
          accountServiceAPI.saveSearchHistory({
            cognitoUserId: user.userId,
            email: user.email,
            searchId: searchResult.requestId,
            searchType: actualSearchType, // Use actual search type charged
            searchRequest: {
              firstName: searchRequest.firstName,
              lastName: searchRequest.lastName,
              amountToCollect: searchRequest.amountToCollect,
              address: searchRequest.address,
              city: searchRequest.city,
              state: searchRequest.state,
              zip: searchRequest.zip,
              phone: searchRequest.phone,
            },
            cost: actualCharge, // Use actual charge amount
            hasResults: true,
            firstName: searchRequest.firstName,
            lastName: searchRequest.lastName,
            searchResult, // Include full search result
          }).catch((saveError) => {
            logger.error('Failed to save search history (non-blocking):', saveError);
          });
        }
      } catch (historyError) {
        logger.error('Error saving search history (non-blocking):', historyError);
      }

      // Clear sessionStorage
      sessionStorage.removeItem(`search_request_${searchId}`);

      // Navigate to results
      navigate(`/search/results/${searchResult.requestId}`);
    } catch (err) {
      // Handle no-hit scenario
      if (err instanceof NoHitError) {
        logger.info('No data found - no wallet charge for no-hit', { searchId });

        // Save no-hit search history (no charge)
        try {
          if (user.userId && user.email) {
            accountServiceAPI.saveSearchHistory({
              cognitoUserId: user.userId,
              email: user.email,
              searchId: searchId || `no_hit_${Date.now()}`,
              searchType: searchRequest.searchType,
              searchRequest: {
                firstName: searchRequest.firstName,
                lastName: searchRequest.lastName,
                amountToCollect: searchRequest.amountToCollect,
                address: searchRequest.address,
                city: searchRequest.city,
                state: searchRequest.state,
                zip: searchRequest.zip,
                phone: searchRequest.phone,
              },
              cost: 0, // No charge for no-hit
              hasResults: false,
              firstName: searchRequest.firstName,
              lastName: searchRequest.lastName,
            }).catch((saveError) => {
              logger.error('Failed to save no-hit search history (non-blocking):', saveError);
            });
          }
        } catch (historyError) {
          logger.error('Error saving no-hit search history (non-blocking):', historyError);
        }

        // Clear sessionStorage
        sessionStorage.removeItem(`search_request_${searchId}`);

        // Show no-hit results
        if (err.aiOverview) {
          handleNoHit(err.message, err.aiOverview);
        } else {
          handleNoHit(err.message, {
            overview: 'No identity match found.',
            riskAssessment: 'Unable to assess risk without identity match.',
            recommendedStrategy: 'Verify debtor information and try searching again.',
          });
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to process wallet-funded search';
        logger.error('Wallet-funded search failed:', err);
        setError(errorMessage);
      }
    } finally {
      setIsCreatingPayment(false);
    }
  };

  const handleConfirmSavedPaymentMethod = async () => {
    if (!searchRequest || !defaultPaymentMethod || !user) {
      return;
    }

    setIsCreatingPayment(true);
    setError(null);

    let paymentIntentId: string | undefined;

    try {
      const price = getSearchTypePrice(searchRequest.searchType);
      const searchTypeName = getSearchTypeDisplayName(searchRequest.searchType);

      logger.info('Creating payment intent with saved payment method', {
        searchId,
        customerEmail: user.email,
        paymentMethodId: defaultPaymentMethod.stripePaymentMethodId,
      });

      // Create payment intent with saved payment method attached
      const paymentResponse = await stripeAPI.createSingleSearchPayment({
        amount: price,
        email: user.email,
        firstName: user.name?.split(' ')[0] || user.name || '',
        lastName: user.name?.split(' ').slice(1).join(' ') || '',
        searchType: searchRequest.searchType,
        amountToCollect: searchRequest.amountToCollect,
        description: `${searchTypeName} - ${searchRequest.firstName} ${searchRequest.lastName}`,
        companyName: user.companyName || '',
        savePaymentMethod: true,
        paymentMethodId: defaultPaymentMethod.stripePaymentMethodId, // Pass saved payment method
      });

      paymentIntentId = paymentResponse.payment_intent_id;

      logger.info('Payment intent created with saved method, executing search', {
        paymentIntentId: paymentResponse.payment_intent_id,
      });

      // Payment authorized with saved method - now execute the search
      const searchResult = await singleSearchAPI.createSearch(searchRequest);

      logger.info('Search completed successfully - determining charge amount', { searchId: searchResult.requestId });

      // Determine actual charge based on what succeeded
      // If scoring_enrich was requested but enrichment failed (no personalInfo), charge for scoring only
      let actualCharge = price;
      let actualSearchType = searchRequest.searchType;

      if (searchRequest.searchType === 'scoring_enrich' && !searchResult.personalInfo) {
        actualCharge = getOneTimeSearchCost('scoring');
        actualSearchType = 'scoring';
        logger.info('Scoring_enrich partial success - charging for scoring only', {
          originalPrice: price,
          actualPrice: actualCharge,
        });
      }

      // Capture the payment (since search was successful and data was found)
      await stripeAPI.capturePayment({
        paymentIntentId: paymentResponse.payment_intent_id,
        actualAmount: actualCharge,
        successfulRows: 1,
        failedRows: 0,
      });

      logger.info('Payment captured successfully', { paymentIntentId: paymentResponse.payment_intent_id, amount: actualCharge, chargedFor: actualSearchType });

      // Save search history to DynamoDB (non-blocking)
      try {
        if (user.userId && user.email) {
          logger.info('Saving search history to DynamoDB', {
            searchId: searchResult.requestId,
            cognitoUserId: user.userId,
            email: user.email
          });

          // Don't await - save in background
          accountServiceAPI.saveSearchHistory({
            cognitoUserId: user.userId,
            email: user.email,
            searchId: searchResult.requestId,
            searchType: actualSearchType, // Use actual search type charged
            searchRequest: {
              firstName: searchRequest.firstName,
              lastName: searchRequest.lastName,
              amountToCollect: searchRequest.amountToCollect,
              address: searchRequest.address,
              city: searchRequest.city,
              state: searchRequest.state,
              zip: searchRequest.zip,
              phone: searchRequest.phone,
            },
            cost: actualCharge, // Use actual charge amount
            hasResults: true,
            firstName: searchRequest.firstName,
            lastName: searchRequest.lastName,
            searchResult, // Include full search result with recovery scenarios
          }).catch((saveError) => {
            logger.error('Failed to save search history (non-blocking):', saveError);
          });
        }
      } catch (historyError) {
        logger.error('Error saving search history (non-blocking):', historyError);
      }

      // Clear sessionStorage
      sessionStorage.removeItem(`search_request_${searchId}`);

      // Navigate to results using the actual search ID from the API
      navigate(`/search/results/${searchResult.requestId}`);
    } catch (err) {
      // Handle no-hit scenario - cancel payment and show message
      if (err instanceof NoHitError) {
        logger.info('No data found, canceling payment authorization', { searchId, paymentIntentId });

        try {
          // Cancel the payment authorization
          if (paymentIntentId) {
            await stripeAPI.cancelPayment({
              paymentIntentId,
              reason: 'No identity match found',
            });
            logger.info('Payment authorization cancelled - customer not charged', { paymentIntentId });
          }

          // Save no-hit search history to DynamoDB (non-blocking)
          try {
            if (user.userId && user.email) {
              logger.info('Saving no-hit search history to DynamoDB', {
                searchId,
                cognitoUserId: user.userId,
                email: user.email
              });

              // Don't await - save in background
              accountServiceAPI.saveSearchHistory({
                cognitoUserId: user.userId,
                email: user.email,
                searchId: searchId || `no_hit_${Date.now()}`,
                searchType: searchRequest.searchType,
                searchRequest: {
                  firstName: searchRequest.firstName,
                  lastName: searchRequest.lastName,
                  amountToCollect: searchRequest.amountToCollect,
                  address: searchRequest.address,
                  city: searchRequest.city,
                  state: searchRequest.state,
                  zip: searchRequest.zip,
                  phone: searchRequest.phone,
                },
                cost: 0, // No charge for no-hit
                hasResults: false,
                firstName: searchRequest.firstName,
                lastName: searchRequest.lastName,
              }).catch((saveError) => {
                logger.error('Failed to save no-hit search history (non-blocking):', saveError);
              });
            }
          } catch (historyError) {
            logger.error('Error saving no-hit search history (non-blocking):', historyError);
          }

          // Clear sessionStorage
          sessionStorage.removeItem(`search_request_${searchId}`);

          // Show no-hit results
          if (err.aiOverview) {
            handleNoHit(err.message, err.aiOverview);
          } else {
            handleNoHit(err.message, {
              overview: 'No identity match found.',
              riskAssessment: 'Unable to assess risk without identity match.',
              recommendedStrategy: 'Verify debtor information and try searching again.',
            });
          }
        } catch (cancelError) {
          logger.error('Failed to cancel payment authorization:', cancelError);
          setError('An error occurred. Please contact support.');
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to process search with saved payment method';
        logger.error('Payment/search flow failed with saved method:', err);
        setError(errorMessage);
      }
    } finally {
      setIsCreatingPayment(false);
    }
  };

  if (isLoadingSearch) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-collectability-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show no-hit message if search returned no data
  if (noHitMessage) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-10">
          <Card className="p-8">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-slate-600" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2 text-center">No Identity Match Found</h2>
            <p className="text-slate-600 mb-6 text-center">{noHitMessage}</p>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
              <p className="text-sm font-semibold text-green-900 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                You were NOT charged
              </p>
              <p className="text-xs text-green-800 mt-1">
                Your payment authorization has been cancelled. No charges will appear on your statement.
              </p>
            </div>

            {noHitAiOverview && (
              <div className="space-y-4 mb-6">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">AI Overview:</h3>
                  <p className="text-sm text-slate-700">{noHitAiOverview.overview}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">Risk Assessment:</h3>
                  <p className="text-sm text-slate-700">{noHitAiOverview.riskAssessment}</p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">Recommended Strategy:</h3>
                  <p className="text-sm text-slate-700">{noHitAiOverview.recommendedStrategy}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button onClick={() => navigate('/search')} variant="outline" className="flex-1">
                Try Another Search
              </Button>
              <Button onClick={() => navigate('/')} className="flex-1">
                Return Home
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !searchRequest) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-10">
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">❌</span>
            </div>
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Error Loading Payment</h2>
            <p className="text-slate-600 mb-6">{error || 'Search data not found'}</p>
            <Button onClick={() => navigate('/search')}>Back to Search</Button>
          </Card>
        </div>
      </div>
    );
  }

  const price = getSearchTypePrice(searchRequest.searchType);
  const searchTypeName = getSearchTypeDisplayName(searchRequest.searchType);
  const pricingDetails = calculateSearchCost(searchRequest.searchType);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate('/search')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Search
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Complete Your Purchase</h1>
          <p className="text-slate-600 mb-8">Review your search details and complete payment</p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="p-4 sticky top-24">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Order Summary</h2>

                {/* Wallet Savings Banner */}
                {pricingDetails.useWallet && pricingDetails.walletDeduction > 0 && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-xs font-semibold text-green-900 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {pricingDetails.stripeCharge === 0 ? 'Paid from Wallet' : 'Wallet Discount Applied'}
                    </p>
                    <p className="text-xs text-green-800 mt-1">
                      Saving {formatCurrency(pricingDetails.walletDeduction)} with your wallet balance
                    </p>
                  </div>
                )}

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Searching For:</span>
                    <span className="font-medium text-slate-900">
                      {searchRequest.firstName} {searchRequest.lastName}
                    </span>
                  </div>

                  {searchRequest.address && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Address:</span>
                      <span className="font-medium text-slate-900 text-right">
                        {searchRequest.address}
                        {searchRequest.city && `, ${searchRequest.city}`}
                        {searchRequest.state && `, ${searchRequest.state}`}
                        {searchRequest.zip && ` ${searchRequest.zip}`}
                      </span>
                    </div>
                  )}

                  {searchRequest.phone && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Phone:</span>
                      <span className="font-medium text-slate-900">{searchRequest.phone}</span>
                    </div>
                  )}

                  {searchRequest.email && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Email:</span>
                      <span className="font-medium text-slate-900">{searchRequest.email}</span>
                    </div>
                  )}

                  {searchRequest.amountToCollect && (searchRequest.searchType === 'scoring' || searchRequest.searchType === 'scoring_enrich') && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Amount to Collect:</span>
                      <span className="font-medium text-slate-900">
                        {formatCurrency(searchRequest.amountToCollect)}
                      </span>
                    </div>
                  )}

                  <div className="border-t border-slate-200 pt-3">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-600">Search Type:</span>
                      <span className="font-medium text-slate-900">{searchTypeName}</span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4 mb-6">
                  {/* Show pricing breakdown if using wallet */}
                  {pricingDetails.useWallet && pricingDetails.walletDeduction > 0 ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>Search Price:</span>
                        <span>{formatCurrency(pricingDetails.total)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Wallet Balance Applied:</span>
                        <span>-{formatCurrency(pricingDetails.walletDeduction)}</span>
                      </div>
                      <div className="border-t border-slate-200 pt-2">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-slate-900">
                            {pricingDetails.stripeCharge === 0 ? 'Total (Paid)' : 'Total Due:'}
                          </span>
                          <span className="text-2xl font-bold text-collectability-blue">
                            {formatCurrency(pricingDetails.stripeCharge)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-slate-900">Total:</span>
                      <span className="text-2xl font-bold text-collectability-blue">{formatCurrency(pricingDetails.total)}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Lock className="w-4 h-4" />
                  <span>Secure payment processing</span>
                </div>
              </Card>
            </div>

            {/* Customer Info & Payment Form */}
            <div className="lg:col-span-2 space-y-3">
              {/* Wallet-Only Payment for authenticated users with sufficient balance */}
              {isAuthenticated && pricingDetails.stripeCharge === 0 && pricingDetails.useWallet && !clientSecret ? (
                <Card className="p-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-semibold text-slate-900 mb-2">Pay with Wallet Balance</h2>
                    <p className="text-slate-600">
                      You have sufficient wallet balance to complete this search
                    </p>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Search Cost:</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(pricingDetails.walletDeduction)}</span>
                      </div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Current Wallet Balance:</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(walletBalance)}</span>
                      </div>
                      <div className="border-t border-slate-200 pt-2 mt-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Balance After Search:</span>
                          <span className="font-semibold text-green-600">{formatCurrency(walletBalance - pricingDetails.walletDeduction)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-semibold text-green-900 mb-2">Benefits:</p>
                      <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                        <li>Instant processing - no payment authorization needed</li>
                        <li>Wallet pricing ({formatCurrency(pricingDetails.walletDeduction)} vs {formatCurrency(getOneTimeSearchCost(searchRequest.searchType))} one-time)</li>
                        <li>Faster checkout experience</li>
                      </ul>
                    </div>

                    {/* FCRA Acknowledgment */}
                    <label className="flex items-start gap-3 text-sm text-slate-600 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <input
                        type="checkbox"
                        checked={walletFcraAcknowledgment}
                        onChange={(e) => setWalletFcraAcknowledgment(e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
                        required
                      />
                      <span>
                        I acknowledge that CollectABILITY is <span className="font-semibold">not a Consumer Reporting Agency (CRA)</span> as defined by the Fair Credit Reporting Act (FCRA), and that I will not use this service for employment, credit, tenant screening, or any FCRA-related purpose.
                      </span>
                    </label>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => setUseSavedPaymentMethod(true)}
                      variant="outline"
                      className="flex-1"
                      disabled={isCreatingPayment}
                    >
                      Use Card Instead
                    </Button>
                    <Button
                      onClick={handleWalletPayment}
                      className="flex-1"
                      disabled={isCreatingPayment || !walletFcraAcknowledgment}
                    >
                      {isCreatingPayment ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Complete Search
                        </>
                      )}
                    </Button>
                  </div>

                  {error && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}
                </Card>
              ) : isAuthenticated && defaultPaymentMethod && useSavedPaymentMethod && !clientSecret ? (
                <Card className="p-8">
                  <SavedPaymentMethodConfirmation
                    paymentMethod={defaultPaymentMethod}
                    estimatedCost={price}
                    maxPossibleCost={price}
                    onConfirm={handleConfirmSavedPaymentMethod}
                    onUseDifferentMethod={() => setUseSavedPaymentMethod(false)}
                    isProcessing={isCreatingPayment}
                    mode="search"
                  />
                  {error && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}
                </Card>
              ) : (
                <>
                  {/* Customer Information Form */}
                  {!clientSecret && (
                    <Card className="p-8">
                  <h2 className="text-xl font-semibold text-slate-900 mb-2">Billing Information</h2>
                  <p className="text-sm text-slate-600 mb-6">Enter your information to proceed with payment</p>

                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}

                  <form onSubmit={handleCustomerInfoSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="customerName" className="block text-sm font-medium text-slate-700 mb-2">
                        Your Full Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="customerName"
                          type="text"
                          placeholder="John Doe"
                          value={customerInfo.name}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                          className={cn('pl-10', customerInfoErrors.name && 'border-red-500')}
                          disabled={isCreatingPayment}
                        />
                      </div>
                      {customerInfoErrors.name && (
                        <p className="mt-1 text-sm text-red-600">{customerInfoErrors.name}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="customerEmail" className="block text-sm font-medium text-slate-700 mb-2">
                        Your Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <Input
                          id="customerEmail"
                          type="email"
                          placeholder="john@example.com"
                          value={customerInfo.email}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                          className={cn('pl-10', customerInfoErrors.email && 'border-red-500')}
                          disabled={isCreatingPayment}
                        />
                      </div>
                      {customerInfoErrors.email && (
                        <p className="mt-1 text-sm text-red-600">{customerInfoErrors.email}</p>
                      )}
                      <p className="mt-1 text-xs text-slate-500">Receipt will be sent to this email</p>
                    </div>

                    <div>
                      <label htmlFor="customerCompany" className="block text-sm font-medium text-slate-700 mb-2">
                        Company Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="customerCompany"
                        type="text"
                        placeholder="Your Company Name"
                        value={customerInfo.companyName}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, companyName: e.target.value })}
                        className={cn(customerInfoErrors.companyName && 'border-red-500')}
                        disabled={isCreatingPayment}
                      />
                      {customerInfoErrors.companyName && (
                        <p className="mt-1 text-sm text-red-600">{customerInfoErrors.companyName}</p>
                      )}
                    </div>

                    {!isAuthenticated && (
                      <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
                          <span className="font-semibold text-slate-700">
                            {accountMode === 'create' ? 'Create a CollectABILITY account' : 'Sign in to your CollectABILITY account'}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const nextMode = accountMode === 'create' ? 'login' : 'create';
                              setAccountMode(nextMode);
                              setAccountPasswordError(null);
                              setLoginError(null);
                              setAccountFeedback(null);
                            }}
                            className="text-slate-600 hover:text-blue-800 font-medium transition-colors"
                          >
                            {accountMode === 'create' ? 'Already have an account? Sign in' : 'Need an account? Create one instead'}
                          </button>
                        </div>

                        {accountMode === 'create' ? (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-semibold text-slate-700 mb-1">Create a password</label>
                                <input
                                  type="password"
                                  value={accountPassword}
                                  onChange={(e) => setAccountPassword(e.target.value)}
                                  placeholder="At least 8 characters"
                                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                  autoComplete="new-password"
                                  disabled={isCreatingPayment}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-slate-700 mb-1">Confirm password</label>
                                <input
                                  type="password"
                                  value={accountPasswordConfirm}
                                  onChange={(e) => setAccountPasswordConfirm(e.target.value)}
                                  placeholder="Re-enter password"
                                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                  autoComplete="new-password"
                                  disabled={isCreatingPayment}
                                />
                              </div>
                            </div>
                            <p className="text-xs text-slate-600">
                              We&apos;ll create your account and sign you in automatically so this search is saved to your dashboard.
                            </p>
                          </>
                        ) : (
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                              Account password <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="password"
                              value={loginPassword}
                              onChange={(e) => setLoginPassword(e.target.value)}
                              placeholder="Enter your password to sign in"
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              autoComplete="current-password"
                              disabled={isCreatingPayment}
                            />
                            <p className="text-xs text-slate-600 mt-2">
                              We&apos;ll sign you in before authorizing payment so this search links to your existing account.
                            </p>
                          </div>
                        )}

                        {accountPasswordError && (
                          <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-md">
                            {accountPasswordError}
                          </div>
                        )}

                        {loginError && (
                          <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-md">
                            {loginError}
                          </div>
                        )}

                        {accountFeedback && (
                          <div className="bg-green-50 border border-green-200 text-green-700 text-xs px-3 py-2 rounded-md">
                            {accountFeedback}
                          </div>
                        )}
                      </div>
                    )}

                    <Button type="submit" size="lg" className="w-full" disabled={isCreatingPayment}>
                      {isCreatingPayment ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Loading Payment Form...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Continue to Payment
                        </>
                      )}
                    </Button>
                  </form>
                </Card>
              )}

              {/* Stripe Payment Form */}
              {clientSecret && (
                <Card className="p-8">
                  <h2 className="text-xl font-semibold text-slate-900 mb-6">Payment Information</h2>

                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: 'stripe',
                        variables: {
                          colorPrimary: '#0066cc',
                        },
                      },
                    }}
                  >
                    <SearchPaymentCheckoutForm
                      searchRequest={searchRequest}
                      searchId={searchId!}
                      customerInfo={customerInfo}
                      savePaymentMethod={savePaymentMethod}
                      onSavePaymentMethodChange={setSavePaymentMethod}
                      cognitoUserId={user?.userId}
                      isAuthenticated={isAuthenticated}
                      onSuccess={handlePaymentSuccess}
                      onNoHit={handleNoHit}
                    />
                  </Elements>
                </Card>
              )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

